// The "Popular" filter: which events people actually open, off a table
// compiled from PostHog rather than anything the browser writes. See
// supabase/migrations/20260925230000_popularity_by_event.sql for the rule and
// why it is keyed on the event row rather than the event name.
//
// THE RULE (Andrew, 2026-09-25). Popular points people at distinct events, and
// the unit is ONE dated event. Each date of a weekly repeat counts on its own:
// this Wednesday's karaoke and next Wednesday's never add together. The one
// exception is a multi-day event (a fair, a festival, a play's run), whose days
// share a `parent_id` and add up to one total.
//
// This module owns the bar, the sentinel that lets Popular ride in the same
// `selected` Set a real host category uses (App.tsx / FilterDrawer), the fetch,
// and the ranking. The sentinel is the same trick lib/favorites plays with
// FAVORITES_KIND - see App.tsx's `matched` filter and the ?hosts=popular round
// trip in lib/urlFilters for where it actually gets used.

import { useEffect, useState } from 'react'
import { supabase } from './supabase'
import { isPastEvent } from './dates'
import type { EventRow } from '../types'

/**
 * Distinct people in the last 45 days an event needs before Popular counts it,
 * and how many upcoming events Popular shows at most. Set off the real
 * distribution once counts moved from names to single events (2026-09-25):
 * over 9/11-9/25 the most-opened single event had 8 opens from 5 people and
 * none reached 10, so the old 10-opens bar would have shown nothing. People,
 * not opens, because one visitor reopening a card is not popularity (Open Mic
 * at Big Lick: 4 opens, 1 person).
 */
export const POPULAR_MIN_PEOPLE = 3
export const POPULAR_TOP_N = 15

/**
 * Sentinel for the same `selected` Set the real host categories use, the same
 * trick lib/favorites plays with FAVORITES_KIND. Double-underscored so it can
 * never collide with a real host name - every real one is a plain human
 * category ("Live Music", "Comedy"). slugify() turns it into the readable
 * `?hosts=popular` on the way into the URL.
 */
export const POPULAR_KIND = '__popular__'

/**
 * What Popular counts as one event: a multi-day event's days share their first
 * day's id through parent_id; everything else is its own row.
 */
export function popularKey(e: Pick<EventRow, 'id' | 'parent_id'>): string {
  return e.parent_id ?? e.id
}

interface OpensRow {
  event_id: string
  people_45d: number
}

/**
 * event id -> people_45d for every event anyone opened. Not thresholded on the
 * server: a multi-day event's days are summed before the bar applies, so a
 * fair with 2 people on each of 4 days has to arrive as four rows of 2.
 * A few hundred rows of a uuid and an int, fetched once - the table is
 * refreshed every three days by a scheduled pass, never by the browser.
 *
 * A failed fetch returns an empty map, which hides the Popular row entirely
 * (see popularCount in App.tsx) rather than offering a checkbox that can't
 * filter anything - the same failure shape as an empty Featured or Picks list.
 */
export function useEventPeople(): Map<string, number> {
  const [people, setPeople] = useState<Map<string, number>>(new Map())

  useEffect(() => {
    let cancelled = false
    void supabase
      .from('lyh_event_opens')
      .select('event_id,people_45d')
      .gt('people_45d', 0)
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('load event opens', error)
          return
        }
        setPeople(new Map((data as OpensRow[]).map((r) => [r.event_id, r.people_45d])))
      })
    return () => {
      cancelled = true
    }
  }, [])

  return people
}

/**
 * The Popular set: popularKey -> people, for at most POPULAR_TOP_N events that
 * still have an upcoming date and clear POPULAR_MIN_PEOPLE. A multi-day event
 * sums its days (past days included, so a fair keeps the people who opened
 * Thursday once Thursday is over); a single event is just its own row.
 * Ties go to the sooner event, so the shortlist leans toward what's coming up.
 *
 * `events` must be ascending by start_time (useApprovedEvents' order), or
 * "sooner" stops meaning sooner.
 */
export function rankPopular(
  events: readonly EventRow[],
  peopleById: ReadonlyMap<string, number>,
  now: Date,
): Map<string, number> {
  if (peopleById.size === 0) return new Map()
  const people = new Map<string, number>()
  const soonest = new Map<string, number>()
  for (const [i, e] of events.entries()) {
    const key = popularKey(e)
    const n = peopleById.get(e.id)
    if (n) people.set(key, (people.get(key) ?? 0) + n)
    if (!soonest.has(key) && !isPastEvent(e, now)) soonest.set(key, i)
  }
  const ranked = [...soonest.keys()]
    .filter((k) => (people.get(k) ?? 0) >= POPULAR_MIN_PEOPLE)
    .sort((a, b) => people.get(b)! - people.get(a)! || soonest.get(a)! - soonest.get(b)!)
    .slice(0, POPULAR_TOP_N)
  return new Map(ranked.map((k) => [k, people.get(k)!]))
}

/**
 * Collapse a multi-day event to its soonest day, leaving every other row
 * untouched. A single event is already one row, so in practice this only folds
 * the days of a fair or a play's run - without it the Amherst County Fair
 * would take four of the fifteen places.
 *
 * Only rows `popular` actually knows about get folded. Popular rides in the
 * same `selected` Set a real category does (see App.tsx), so ticking it
 * alongside one unions the two - and an ordinary category's rows must list in
 * full.
 *
 * `events` must already be ascending by start_time (App.tsx's `events` is,
 * and filtering preserves order), or "first seen" stops meaning "soonest".
 */
export function collapseToNextOccurrence(
  events: EventRow[],
  popular: ReadonlyMap<string, number>,
): EventRow[] {
  const seen = new Set<string>()
  const out: EventRow[] = []
  for (const e of events) {
    const key = popularKey(e)
    if (!popular.has(key)) {
      out.push(e)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push(e)
  }
  return out
}
